import * as Yup from 'yup';
import { QuestionGroupLimits } from '@/enums';
import {
  isQuestion,
  splitIntoCheckpointBlocks,
  countQuestionsInBlock,
} from '@/utils/questionGroupHelpers';

/**
 * Validation schema for individual interview questions
 */
const questionSchema = Yup.object({
  id: Yup.string().required('msg.idRequired'),
  question: Yup.string().trim().required('msg.thisFieldIsRequired'),
  media: Yup.mixed(),
  follow_up_question: Yup.string().nullable(),
  probing_goal: Yup.string().nullable(),
  answer_quantification: Yup.array().of(
    Yup.object({
      quantify_answer: Yup.boolean(),
      value: Yup.string().nullable(),
    }),
  ),
});

/**
 * Validation schema for a checkpoint node (embed-only, root-level only)
 */
const checkpointSchema = Yup.object({
  id: Yup.string().required('msg.idRequired'),
  type: Yup.string().oneOf(['checkpoint']).required(),
  label: Yup.string().trim().required('msg.checkpointLabelRequired'),
});

/**
 * Validation schema for interview question groups (with recursive subgroup support)
 * @param isRoot - Whether this is the root group (root groups don't require an id)
 * @param depth - Nesting depth (0 = root, 1 = first-level group, 2+ = subgroup)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createGroupSchema = (isRoot = false, depth = 0): Yup.ObjectSchema<any> =>
  Yup.object({
    ...(isRoot
      ? {} // root doesn't need id
      : { id: Yup.string().required('msg.idRequired') }),

    name: Yup.string().trim().required('msg.groupNameRequired'),

    description: Yup.string().nullable(),
    randomize: Yup.boolean(),

    children: Yup.array()
      .of(
        Yup.lazy((child) => {
          if (!child || typeof child !== 'object') return Yup.mixed();
          if ('question' in child) return questionSchema;
          if ('children' in child) return createGroupSchema(false, depth + 1); // subgroup
          // TELV1-421 — checkpoint node
          if ((child as { type?: unknown }).type === 'checkpoint') {
            if (depth === 0) return checkpointSchema; // root-level: valid
            // depth > 0: checkpoints not allowed inside groups
            return Yup.mixed().test(
              'no-nested-checkpoint',
              'msg.checkpointsOnlyAtRootLevel',
              () => false,
            );
          }
          return Yup.mixed();
        }),
      )
      // Root group can be empty, but nested groups/subgroups must have at least one child
      .test('not-empty', (val, context) => {
        if (isRoot) return true;
        if (!Array.isArray(val) || val.length === 0) {
          // depth 1 = first-level group, depth 2+ = subgroup
          const errorKey = depth === 1 ? 'msg.groupCannotBeEmpty' : 'msg.subgroupCannotBeEmpty';
          return context.createError({ message: errorKey });
        }
        return true;
      })
      // TELV1-421 — at root level, reject trees where a checkpoint is adjacent to another
      // checkpoint with no question in between, or is at the leading/trailing edge with
      // no question on one side.
      .test('no-adjacent-empty-checkpoint-segment', (val, context) => {
        if (!isRoot) return true;
        if (!Array.isArray(val) || val.length === 0) return true;

        const blocks = splitIntoCheckpointBlocks(val);
        // Only one block means no checkpoints — nothing to validate.
        if (blocks.length <= 1) return true;

        // Every block between consecutive checkpoints (including the leading and
        // trailing edges) must contain at least one question.
        const allBlocksHaveQuestion = blocks.every((block) => block.some(isQuestion));
        if (!allBlocksHaveQuestion) {
          return context.createError({ message: 'msg.checkpointNeedsQuestionsBothSides' });
        }
        return true;
      }),
  });

/**
 * Root-level schema for interview questions structure
 */
const interviewQuestionsSchema = Yup.lazy((value) => {
  if (!value) return Yup.mixed().nullable();
  return createGroupSchema(true, 0); // root group (no id required, depth 0)
});

/**
 * Validation schema for interview questions with embed-specific structure constraints.
 * For interview projects, returns the standard interviewQuestionsSchema.
 *
 * TELV1-457 — embed projects are capped per block rather than on a flat total:
 *   - at most EMBED_MAX_QUESTIONS_PER_BLOCK questions in any single block
 *     (the run of questions between two checkpoints), and
 *   - at most EMBED_MAX_CHECKPOINTS checkpoints (so at most EMBED_MAX_BLOCKS
 *     blocks). There is no hard total cap.
 */
export const createInterviewQuestionsSchema = (isEmbedProject: boolean) => {
  if (!isEmbedProject) return interviewQuestionsSchema;

  return Yup.lazy((value) => {
    if (!value) return Yup.mixed().nullable();
    return createGroupSchema(true, 0)
      .test('embed-max-questions-per-block', 'msg.embedBlockMaxQuestionsReached', (group) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const children = (group as any)?.children;
        if (!Array.isArray(children)) return true;
        return splitIntoCheckpointBlocks(children).every(
          (block) =>
            countQuestionsInBlock(block) <= QuestionGroupLimits.EMBED_MAX_QUESTIONS_PER_BLOCK,
        );
      })
      .test('embed-max-checkpoints', 'msg.embedMaxCheckpointsReached', (group) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const children = (group as any)?.children;
        if (!Array.isArray(children)) return true;
        // blocks === checkpoints + 1, so the block cap maps directly to a marker cap.
        return splitIntoCheckpointBlocks(children).length <= QuestionGroupLimits.EMBED_MAX_BLOCKS;
      });
  });
};

/**
 * Main validation schema for project creation/update
 * @param min - Minimum number of respondents (used for published projects)
 * @param isProjectPublished - Whether the project is already published
 * @param isEmbedProject - Whether this is an embed project (TELV1-186)
 */
/**
 * Screening questions schema — only used for interview projects.
 * TELV1-186: embed projects skip screening validation entirely.
 */
const screeningQuestionsSchema = Yup.array().of(
  Yup.object()
    .shape({
      id: Yup.string(),
      question: Yup.string().trim().required('msg.thisFieldIsRequired'),
      type: Yup.string(),
      quotas_enabled: Yup.boolean(),
      // Media (stimuli) is server-managed (uploaded via its own endpoint);
      // the form only round-trips label/order edits — nothing to validate.
      media: Yup.mixed().nullable(),
      responses: Yup.lazy((value) =>
        Array.isArray(value)
          ? Yup.array()
              .of(
                Yup.object().shape({
                  id: Yup.string(),
                  value: Yup.string().trim().required('msg.thisFieldIsRequired'),
                  rule: Yup.string().nullable().required('msg.pleaseSelectOneOfTheRules'),
                  quota_percentage: Yup.number().nullable(),
                }),
              )
              .nullable()
          : Yup.mixed().nullable(),
      ),
      numericResponse: Yup.lazy((value) =>
        typeof value === 'object' && value !== null
          ? Yup.object().shape({
              minimum: Yup.number()
                .nullable()
                .transform((val) => (Number.isNaN(val) ? null : val)),
              maximum: Yup.number()
                .nullable()
                .transform((val) => (Number.isNaN(val) ? null : val)),
            })
          : Yup.mixed().nullable(),
      ),
    })
    .test('quota-sum-100', 'msg.quotaPercentagesMustEqual100', (question) => {
      if (!question?.quotas_enabled) return true;
      const responses = question?.responses;
      if (!Array.isArray(responses)) return true;
      const qualifyingResponses = responses.filter((r) => r?.rule === 'qualify');
      if (qualifyingResponses.length === 0) return true;
      const sum = qualifyingResponses.reduce(
        (acc, r) => acc + (Number(r?.quota_percentage) || 0),
        0,
      );
      // Round to 3 decimal places before comparing to avoid floating-point precision
      // issues (e.g. 33.33 + 33.33 + 33.34 raw sum may drift just above the 0.01 tolerance)
      return Math.abs(Math.round(sum * 1000) / 1000 - 100) <= 0.01;
    }),
);

export const createProjectValidationSchema = (
  min: number,
  isProjectPublished: boolean,
  isEmbedProject: boolean = false,
) => {
  return Yup.object().shape({
    type_of_interview: Yup.array().of(Yup.string()),
    interviewer_name: Yup.string(),
    company_name: Yup.string(),
    interview_goal: Yup.string(),
    interview_audience: Yup.string(),
    tone_of_voice: Yup.string(),
    privacy_policy: Yup.string().url('msg.pleaseEnterValidUrl'),
    language: Yup.string(),
    analysis_language: Yup.string(),
    allow_language_selection: Yup.boolean().nullable(),
    theme_id: Yup.string(),
    snippets: Yup.array(),
    // TELV1-186: embed projects have no opening_message — allow empty.
    // Interview projects: unchanged (no explicit required() here either,
    // the required check lives in the ProjectRequiredFieldsStore / publish guard).
    opening_message: Yup.string(),
    respondents_max: Yup.number()
      .required('msg.thisFieldIsRequired')
      .min(
        isProjectPublished ? min : 0,
        isProjectPublished
          ? `msg.numberMustBeGreaterThanOrEqualTo`
          : 'msg.numberMustBeGreaterThanZero',
      )
      .max(1000000, 'msg.numberMustBeLessThanOrEqualTo1000000')
      .nullable()
      .typeError('msg.thisFieldIsRequired'),
    interview_questions: createInterviewQuestionsSchema(isEmbedProject),
    // TELV1-186: skip screening question validation for embed projects.
    // Embed projects never have screening questions; always pass Yup.mixed()
    // to keep the form schema shape stable (conditional spread or .concat()
    // would widen the TypeScript return type and break yupResolver inference).
    screening_questions: isEmbedProject ? Yup.mixed() : screeningQuestionsSchema,
  });
};
